/**
 * Trust Flow UI V1 - TrustFlowPanel
 * 
 * Surfaces unified continuity verification in a simple, user-friendly way
 * without exposing internal system details.
 * 
 * BOUNDARIES:
 * - Read-only UI: does not affect execution
 * - No internal data exposure
 * - No hash computation
 * - Only reflects validated graph state
 * 
 * INVARIANT:
 * TrustFlowPanel is a read-only display component that shows continuity status.
 */

import { useState } from 'react';

export interface TrustFlowStep {
  label: string;
  status: 'ok' | 'missing' | 'invalid';
}

export interface TrustFlowPanelProps {
  graphStatus: 'verified' | 'partially_verified' | 'invalid';
  steps: TrustFlowStep[];
}

export function TrustFlowPanel({ graphStatus, steps }: TrustFlowPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getStatusIndicator = () => {
    switch (graphStatus) {
      case 'verified':
        return '🟢 Verified process';
      case 'partially_verified':
        return '🟡 Partially verified';
      case 'invalid':
        return '🔴 Cannot verify';
      default:
        return '';
    }
  };

  const getStatusIcon = (status: 'ok' | 'missing' | 'invalid') => {
    switch (status) {
      case 'ok':
        return '✔';
      case 'missing':
        return '○';
      case 'invalid':
        return '✗';
      default:
        return '';
    }
  };

  const getStatusColor = (status: 'ok' | 'missing' | 'invalid') => {
    switch (status) {
      case 'ok':
        return 'text-green-600';
      case 'missing':
        return 'text-yellow-600';
      case 'invalid':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="border rounded-lg bg-white shadow-sm">
      {/* Compact indicator */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        type="button"
      >
        <span className="text-sm font-medium">{getStatusIndicator()}</span>
        <span className="text-gray-400">
          {isExpanded ? '▼' : '▶'}
        </span>
      </button>

      {/* Expandable panel */}
      {isExpanded && (
        <div className="border-t px-4 py-3 bg-gray-50">
          <div className="space-y-2">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center gap-3 text-sm">
                <span className={getStatusColor(step.status)}>
                  {getStatusIcon(step.status)}
                </span>
                <span className={step.status === 'missing' ? 'text-gray-400' : 'text-gray-700'}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
